import { useApiQuery } from '@/hooks/useApiQuery';
import { useUser } from '@/hooks/useUser';
import { api } from '@/lib/api-client';
import type { ViewHistoryItem } from '@/types/view';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { RecentSearchItem } from './RecentSearchItem';

export function RecentSearches() {
  const { user, isLoading } = useUser();
  const userId = user?.user_id || 'anonymous';

  const safeUserId = userId || '';
  const queryClient = useQueryClient();
  function invalidateHistory() {
    queryClient.invalidateQueries({ queryKey: ['view-history', safeUserId] });
  }
  const { data, isLoading: apiLoading } = useApiQuery(
    ['view-history', safeUserId],
    () => api.getViewHistory(safeUserId, 20),
    { enabled: !!userId },
  );

  const history: ViewHistoryItem[] = useMemo(() => {
    if (!data) return [];
    const raw = (data.history || data.views) ?? [];
    // Deduplicate by token_address, keeping only the most recent per token
    const seen = new Set<string>();
    const deduped: ViewHistoryItem[] = [];
    for (const item of raw) {
      if (!seen.has(item.token_address)) {
        deduped.push(item);
        seen.add(item.token_address);
      }
    }
    return deduped;
  }, [data]);

  if (isLoading) return null;
  return (
    <>
      <Card className="w-full max-w-xl mx-auto mt-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Recent Searches</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="flex flex-col gap-2">
            {apiLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="skeleton h-10 rounded bg-gray-800" />
              ))
            ) : history.length === 0 ? (
              <li className="text-gray-500 text-sm">No recent searches.</li>
            ) : (
              history
                .slice(0, 5)
                .map(item => (
                  <RecentSearchItem
                    key={item.created_at + '-' + String(item.token_address)}
                    item={item}
                    onInvalidate={invalidateHistory}
                  />
                ))
            )}
          </ul>
        </CardContent>
      </Card>
    </>
  );
}
